import type { InventorySupplier, InventoryRoom, InventoryRoomAccess, InventoryRoomRequest, InventoryPurchaseOrder, InventoryIssueBatch, InventorySyncLog, StockTakeSession, StockTakeLine } from '../services/inventoryService';

import type { Json } from '../integrations/supabase/types';

export type QueryResult = { data: unknown; error: { message: string } | null };
type Decoder<T> = (value: unknown, path: string) => T;

const invalid = (path: string): never => { throw new Error(path); };
const record: Decoder<Record<string, unknown>> = (value, path) => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return invalid(path);
  return value as Record<string, unknown>;
};
const string: Decoder<string> = (value, path) => typeof value === 'string' ? value : invalid(path);
const boolean: Decoder<boolean> = (value, path) => typeof value === 'boolean' ? value : invalid(path);
const number: Decoder<number> = (value, path) => {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : invalid(path);
};
const json: Decoder<Json> = (value, path) => {
  if (value === null) return null;
  if (typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : invalid(path);
  if (Array.isArray(value)) return value.map((entry: unknown, index: number) => json(entry, `${path}[${index}]`));
  const object = record(value, path);
  return Object.fromEntries(Object.entries(object).map(([key, entry]) => [key, json(entry, `${path}.value`)]));
};
const nullable = <T>(decode: Decoder<T>): Decoder<T | null> => (value, path) => value === null ? null : decode(value, path);
const member = <T extends string>(values: readonly T[]): Decoder<T> => (value, path) => {
  for (const item of values) if (item === value) return item;
  return invalid(path);
};
export const rows = <T>(decode: Decoder<T>): Decoder<T[]> => (value, path) => {
  if (!Array.isArray(value)) return invalid(path);
  return value.map((row: unknown, index: number) => decode(row, `${path}[${index}]`));
};
export const records = rows(record);
export const first = <T>(values: readonly T[]): T | null => values[0] ?? null;

export const requireSuccess = (label: string, result: QueryResult): void => {
  if (result.error) throw new Error(`${label}.error`);
};
export const requireData = <T>(label: string, result: QueryResult, decode: Decoder<T>): T => {
  requireSuccess(label, result);
  return decode(result.data, label);
};
export const optionalMigrationData = <T>(label: string, result: QueryResult, fallback: T, decode: Decoder<T>): T => {
  if (result.error) {
    const message = result.error.message.toLowerCase();
    if (message.includes('does not exist') || message.includes('could not find')) {
      console.warn(`${label}: optional migration table is not available yet.`);
      return fallback;
    }
  }
  return requireData(label, result, decode);
};

export const decodeSupplier = (value: unknown, path: string): InventorySupplier => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    plato_supplier_id: nullable(string)(row.plato_supplier_id, `${path}.plato_supplier_id`),
    name: string(row.name, `${path}.name`),
    email: nullable(string)(row.email, `${path}.email`),
    telephone: nullable(string)(row.telephone, `${path}.telephone`),
    handphone: nullable(string)(row.handphone, `${path}.handphone`),
    contact_person: nullable(string)(row.contact_person, `${path}.contact_person`),
    address: nullable(string)(row.address, `${path}.address`),
    notes: nullable(string)(row.notes, `${path}.notes`),
    created_at: string(row.created_at, `${path}.created_at`),
    updated_at: string(row.updated_at, `${path}.updated_at`),
  };
};

export const decodeRoom = (value: unknown, path: string): InventoryRoom => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    name: string(row.name, `${path}.name`),
    clinic: string(row.clinic, `${path}.clinic`),
    clinic_id: nullable(string)(row.clinic_id, `${path}.clinic_id`),
    color: nullable(string)(row.color, `${path}.color`),
    status: string(row.status, `${path}.status`),
  };
};

export const decodeRoomAccess = (value: unknown, path: string): InventoryRoomAccess => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    user_id: string(row.user_id, `${path}.user_id`),
    room_id: string(row.room_id, `${path}.room_id`),
    can_request: boolean(row.can_request, `${path}.can_request`),
  };
};

export const decodeRoomRequest = (value: unknown, path: string): InventoryRoomRequest => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    room_id: string(row.room_id, `${path}.room_id`),
    requested_by: string(row.requested_by, `${path}.requested_by`),
    status: member(['draft', 'submitted', 'reviewing', 'partially_fulfilled', 'fulfilled', 'cancelled'] as const)(row.status, `${path}.status`),
    request_notes: nullable(string)(row.request_notes, `${path}.request_notes`),
    submitted_at: nullable(string)(row.submitted_at, `${path}.submitted_at`),
    reviewed_at: nullable(string)(row.reviewed_at, `${path}.reviewed_at`),
    reviewed_by: nullable(string)(row.reviewed_by, `${path}.reviewed_by`),
    created_at: string(row.created_at, `${path}.created_at`),
    updated_at: string(row.updated_at, `${path}.updated_at`),
  };
};

export const decodePurchaseOrder = (value: unknown, path: string): InventoryPurchaseOrder => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    supplier_id: string(row.supplier_id, `${path}.supplier_id`),
    po_number: string(row.po_number, `${path}.po_number`),
    status: member(['draft', 'ready', 'sent', 'acknowledged', 'cancelled'] as const)(row.status, `${path}.status`),
    created_by: string(row.created_by, `${path}.created_by`),
    sent_at: nullable(string)(row.sent_at, `${path}.sent_at`),
    sent_to_email: nullable(string)(row.sent_to_email, `${path}.sent_to_email`),
    supplier_message: nullable(string)(row.supplier_message, `${path}.supplier_message`),
    created_at: string(row.created_at, `${path}.created_at`),
    updated_at: string(row.updated_at, `${path}.updated_at`),
  };
};

export const decodeIssueBatch = (value: unknown, path: string): InventoryIssueBatch => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    batch_number: string(row.batch_number, `${path}.batch_number`),
    status: member(['planned', 'ready_for_picking', 'partially_issued', 'issued', 'plato_failed', 'cancelled'] as const)(row.status, `${path}.status`),
    created_by: string(row.created_by, `${path}.created_by`),
    confirmed_by: nullable(string)(row.confirmed_by, `${path}.confirmed_by`),
    confirmed_at: nullable(string)(row.confirmed_at, `${path}.confirmed_at`),
    issue_notes: nullable(string)(row.issue_notes, `${path}.issue_notes`),
    plato_adjustment_status: member(['not_started', 'pending', 'succeeded', 'failed', 'skipped'] as const)(row.plato_adjustment_status, `${path}.plato_adjustment_status`),
    plato_adjustment_reference: nullable(string)(row.plato_adjustment_reference, `${path}.plato_adjustment_reference`),
    plato_adjustment_error: nullable(string)(row.plato_adjustment_error, `${path}.plato_adjustment_error`),
    plato_adjustment_payload: json(row.plato_adjustment_payload, `${path}.plato_adjustment_payload`),
    plato_adjusted_at: nullable(string)(row.plato_adjusted_at, `${path}.plato_adjusted_at`),
    created_at: string(row.created_at, `${path}.created_at`),
    updated_at: string(row.updated_at, `${path}.updated_at`),
  };
};

export const decodeSyncLog = (value: unknown, path: string): InventorySyncLog => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    direction: member(['plato_to_app', 'app_to_plato'] as const)(row.direction, `${path}.direction`),
    endpoint: string(row.endpoint, `${path}.endpoint`),
    status: member(['success', 'error', 'partial'] as const)(row.status, `${path}.status`),
    summary: string(row.summary, `${path}.summary`),
    error_message: nullable(string)(row.error_message, `${path}.error_message`),
    created_at: string(row.created_at, `${path}.created_at`),
  };
};

export const decodeStockTakeSession = (value: unknown, path: string): StockTakeSession => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    status: member(['open', 'completed'] as const)(row.status, `${path}.status`),
    note: nullable(string)(row.note, `${path}.note`),
    location: nullable(string)(row.location, `${path}.location`),
    created_by: nullable(string)(row.created_by, `${path}.created_by`),
    completed_by: nullable(string)(row.completed_by, `${path}.completed_by`),
    completed_at: nullable(string)(row.completed_at, `${path}.completed_at`),
    created_at: string(row.created_at, `${path}.created_at`),
  };
};

export const decodeStockTakeLine = (value: unknown, path: string): StockTakeLine => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    session_id: string(row.session_id, `${path}.session_id`),
    inventory_item_id: string(row.inventory_item_id, `${path}.inventory_item_id`),
    counted_qty: number(row.counted_qty, `${path}.counted_qty`),
    system_qty_snapshot: number(row.system_qty_snapshot, `${path}.system_qty_snapshot`),
    variance: number(row.variance, `${path}.variance`),
    applied: boolean(row.applied, `${path}.applied`),
    applied_at: nullable(string)(row.applied_at, `${path}.applied_at`),
    created_at: string(row.created_at, `${path}.created_at`),
  };
};

export const decodeId = (value: unknown, path: string) => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
  };
};

export const decodePoNumber = (value: unknown, path: string) => {
  const row = record(value, path);
  return {
    po_number: nullable(string)(row.po_number, `${path}.po_number`),
  };
};

export const decodePurchaseOrderCreated = (value: unknown, path: string) => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    po_number: string(row.po_number, `${path}.po_number`),
  };
};

export const decodeBatchCreated = (value: unknown, path: string) => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    batch_number: string(row.batch_number, `${path}.batch_number`),
  };
};

export const decodeOrderConfig = (value: unknown, path: string) => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    order_unit: nullable(string)(row.order_unit, `${path}.order_unit`),
    pack_size: nullable(number)(row.pack_size, `${path}.pack_size`),
  };
};

export const decodeActiveState = (value: unknown, path: string) => {
  const row = record(value, path);
  return {
    id: string(row.id, `${path}.id`),
    is_active: boolean(row.is_active, `${path}.is_active`),
  };
};

